import { useState } from "react"
import {
  X,
  Shield,
  Download,
  ArrowLeft,
  Info,
  ExternalLink,
} from "lucide-react"
import appleIcon from "../assets/icons/apple.png"
import windowsIcon from "../assets/icons/windows.png"

const MAC_URL =
  "https://storage.googleapis.com/interviewguard-downloads/InterviewGuard.dmg"
const WIN_URL =
  "https://storage.googleapis.com/interviewguard-downloads/InterviewGuard-x64.exe"

const IMG_MAC =
  "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1774182085/mac_pare8j.png"
const IMG_WIN1 =
  "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1774182085/windows1_ks0yki.png"
const IMG_WIN2 =
  "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1774182086/windows2_xij4xm.png"

const StepKey = ({ children }) => (
  <span
    style={{
      display: "inline",
      background: "rgba(0,0,0,0.06)",
      border: "0.5px solid rgba(0,0,0,0.12)",
      borderRadius: 4,
      padding: "1px 6px",
      fontSize: 11.5,
      fontWeight: 500,
      fontFamily: "monospace",
      color: "inherit",
    }}
  >
    {children}
  </span>
)

const DownloadInterviewGuardPage = () => {
  const [selectedOS, setSelectedOS] = useState(null)

  const osCards = [
    {
      os: "mac",
      icon: appleIcon,
      label: "Download for macOS",
      req: "macOS 12 Monterey or later",
    },
    {
      os: "win",
      icon: windowsIcon,
      label: "Download for Windows",
      req: "Windows 10 / 11 (64-bit)",
    },
  ]

  const osConfig = {
    mac: {
      title: "Security notice — macOS",
      pill: "macOS",
      pillBg: "#e8e8ed",
      pillColor: "#1c1c1e",
      dlHref: MAC_URL,
      dlBg: "#1c1c1e",
      intro:
        "macOS will show a security prompt when you open InterviewGuard for the first time. This is normal — here's how to get past it.",
      note: "Apple scanned this disk image for malicious software and none was detected. This prompt appears for all apps downloaded outside the Mac App Store.",
      steps: [
        {
          label: "A prompt will appear asking to confirm",
          desc: "After opening the .dmg, macOS shows this dialog. Apple has already scanned the file and found no malicious software.",
          img: IMG_MAC,
          imgAlt: "macOS open confirmation dialog",
        },
        {
          label: (
            <>
              Click <StepKey>Open</StepKey> to continue
            </>
          ),
          desc: "The app will launch immediately. You won't see this prompt again on the same machine.",
          img: null,
        },
      ],
    },
    win: {
      title: "Security notice — Windows",
      pill: "Windows",
      pillBg: "#cce4f7",
      pillColor: "#004f8c",
      dlHref: WIN_URL,
      dlBg: "#0078d4",
      intro:
        "Windows SmartScreen will flag InterviewGuard because it's a new app. This is expected — follow these two steps to run it.",
      note: "This warning appears because InterviewGuard hasn't yet built enough download history for SmartScreen to auto-trust it. The app is safe to run.",
      steps: [
        {
          label: (
            <>
              Click <StepKey>More info</StepKey> on the blue screen
            </>
          ),
          desc: 'SmartScreen blocks unrecognized apps by default. Click "More info" to reveal the run option.',
          img: IMG_WIN1,
          imgAlt: "Windows SmartScreen — More info",
        },
        {
          label: (
            <>
              Click <StepKey>Run anyway</StepKey>
            </>
          ),
          desc: 'You\'ll see the app name (InterviewGuard.exe) confirmed. Click "Run anyway" and the installer will start.',
          img: IMG_WIN2,
          imgAlt: "Windows SmartScreen — Run anyway",
        },
      ],
    },
  }

  const cfg = selectedOS ? osConfig[selectedOS] : null

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f5f7",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        padding: "40px 16px",
      }}
    >
      {/* ── Header ─────────────────────────────────────────── */}
      <div style={{ textAlign: "center", marginBottom: 36, maxWidth: 420 }}>
        {/* Label pill */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "#fff",
            border: "0.5px solid rgba(0,0,0,0.1)",
            borderRadius: 8,
            padding: "5px 10px 5px 7px",
            marginBottom: 20,
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <Shield size={13} color="#555" />
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: "#666",
            }}
          >
            InterviewGuard
          </span>
        </div>

        {/* Title */}
        <h1
          style={{
            fontSize: 28,
            fontWeight: 600,
            color: "#0a0a0a",
            margin: "0 0 10px",
            letterSpacing: "-0.5px",
            lineHeight: 1.2,
          }}
        >
          Install before your interview
        </h1>

        {/* Subtitle */}
        <p
          style={{
            fontSize: 14,
            color: "#888",
            lineHeight: 1.7,
            margin: "0 0 20px",
            borderRadius: 0,
            fontFamily: "'IBM Plex Mono', monospace",
            fontWeight: 400,
          }}
        >
          A lightweight desktop app that detects AI cheat tools running during
          your session.
        </p>

        {/* Divider */}
        <div
          style={{
            width: 32,
            height: 1,
            background: "rgba(0,0,0,0.12)",
            margin: "0 auto",
          }}
        />
      </div>

      {/* ── OS Cards ───────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          width: "100%",
          maxWidth: 400,
        }}
      >
        {osCards.map(({ os, icon, label, req }) => (
          <button
            key={os}
            onClick={() => setSelectedOS(os)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "16px 18px",
              border: "0.5px solid rgba(0,0,0,0.12)",
              borderRadius: 12,
              background: "#fff",
              cursor: "pointer",
              textAlign: "left",
              width: "100%",
              transition: "box-shadow 0.15s, border-color 0.15s",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"
              e.currentTarget.style.borderColor = "rgba(0,0,0,0.2)"
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)"
              e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)"
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <img
                src={icon}
                alt={os}
                width={30}
                height={30}
                style={{ objectFit: "contain", display: "block" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <p
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#111",
                  margin: 0,
                }}
              >
                {label}
              </p>
              <p
                style={{
                  fontSize: 12,
                  color: "#aaa",
                  margin: "3px 0 0",
                  fontFamily: "monospace",
                }}
              >
                {req}
              </p>
            </div>
            <Download size={15} color="#bbb" />
          </button>
        ))}
      </div>

      <a
        href="https://resolut.co.in/interviewguard"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          marginTop: 20,
          fontSize: 12,
          color: "#aaa",
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        Learn more <ExternalLink size={10} />
      </a>

      {/* ── Warning Modal (inline) ──────────────────────────── */}
      {selectedOS && cfg && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 16px",
          }}
        >
          {/* Backdrop */}
          <div
            onClick={() => setSelectedOS(null)}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              backdropFilter: "blur(2px)",
            }}
          />

          {/* Modal shell */}
          <div
            style={{
              position: "relative",
              zIndex: 1,
              background: "#fff",
              border: "0.5px solid rgba(0,0,0,0.14)",
              borderRadius: 14,
              width: "100%",
              maxWidth: 560,
              maxHeight: "80vh",
              overflow: "hidden",
              boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Modal header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 20px",
                borderBottom: "0.5px solid rgba(0,0,0,0.08)",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Info size={13} color="#999" />
                <span style={{ fontSize: 13, fontWeight: 500, color: "#333" }}>
                  {cfg.title}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    padding: "3px 8px",
                    borderRadius: 4,
                    background: cfg.pillBg,
                    color: cfg.pillColor,
                  }}
                >
                  {cfg.pill}
                </span>
                <button
                  onClick={() => setSelectedOS(null)}
                  style={{
                    width: 26,
                    height: 26,
                    border: "0.5px solid rgba(0,0,0,0.12)",
                    borderRadius: 6,
                    background: "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#888",
                  }}
                >
                  <X size={10} />
                </button>
              </div>
            </div>

            {/* Scrollable body */}
            <div style={{ padding: "20px 22px", overflowY: "auto", flex: 1 }}>
              <p
                style={{
                  fontSize: 13,
                  color: "#666",
                  lineHeight: 1.6,
                  marginBottom: 20,
                }}
              >
                {cfg.intro}
              </p>

              {cfg.steps.map((step, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 14,
                    marginBottom: i < cfg.steps.length - 1 ? 24 : 0,
                  }}
                >
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "#f4f4f4",
                      border: "0.5px solid rgba(0,0,0,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#666",
                      flexShrink: 0,
                      marginTop: 1,
                    }}
                  >
                    {i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#111",
                        marginBottom: 6,
                      }}
                    >
                      {step.label}
                    </p>
                    <p
                      style={{
                        fontSize: 12.5,
                        color: "#666",
                        lineHeight: 1.6,
                        marginBottom: step.img ? 10 : 0,
                      }}
                    >
                      {step.desc}
                    </p>
                    {step.img && (
                      <div
                        style={{
                          borderRadius: 10,
                          overflow: "hidden",
                          border: "0.5px solid rgba(0,0,0,0.1)",
                          background: "#f4f4f4",
                        }}
                      >
                        <img
                          src={step.img}
                          alt={step.imgAlt}
                          style={{ width: "100%", display: "block" }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div
                style={{
                  marginTop: 20,
                  padding: "11px 14px",
                  background: "#f7f7f7",
                  borderLeft: "2px solid rgba(0,0,0,0.12)",
                  borderRadius: "0 8px 8px 0",
                  fontSize: 12,
                  color: "#666",
                  lineHeight: 1.6,
                }}
              >
                {cfg.note}
              </div>
            </div>

            {/* Fixed footer */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "13px 20px",
                borderTop: "0.5px solid rgba(0,0,0,0.08)",
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => setSelectedOS(null)}
                style={{
                  fontSize: 12,
                  color: "#888",
                  background: "transparent",
                  border: "0.5px solid rgba(0,0,0,0.12)",
                  borderRadius: 8,
                  padding: "7px 13px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <ArrowLeft size={11} /> Back
              </button>
              <a
                href={cfg.dlHref}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#fff",
                  background: cfg.dlBg,
                  border: "none",
                  borderRadius: 8,
                  padding: "9px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  textDecoration: "none",
                }}
              >
                <Download size={12} /> Download InterviewGuard
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DownloadInterviewGuardPage
